import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { CampaignRoutingSettings, isCampaignRoutingTargetUsable } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";

import { ROUTING_I18N } from "./utils/campaignRoutingFields";
import { fallbackOutcomeOf, FallbackOutcome } from "./utils/campaignRoutingFallback";
import { ruleSummaryText, RoutingTranslate } from "./utils/campaignRoutingText";
import { RoutingCampaign } from "./utils/campaignRoutingTargets";

interface CampaignRoutingSummaryProps {
	settings: CampaignRoutingSettings | undefined;
	failed: boolean;
	campaigns: readonly RoutingCampaign[];
	onEdit: () => void;
}

const elseLine = (outcome: FallbackOutcome, t: RoutingTranslate): string => {
	switch (outcome.kind) {
		case "DEFAULT":
			return t(`${ROUTING_I18N}.fallback.summaryDefault`, { campaign: outcome.campaignName });
		case "DEFAULT_UNUSABLE":
			return t(`${ROUTING_I18N}.fallback.summaryDefaultUnusable`, {
				campaign: outcome.campaignName,
				state: t(`${ROUTING_I18N}.targetStates.${outcome.state}`),
			});
		case "NO_DEFAULT":
			return t(`${ROUTING_I18N}.fallback.summaryNoDefault`);
		case "NONE":
			return t(`${ROUTING_I18N}.fallback.summaryNone`);
	}
};

const emptyLine = (outcome: FallbackOutcome, t: RoutingTranslate): string => {
	switch (outcome.kind) {
		case "DEFAULT":
			return t(`${ROUTING_I18N}.summary.emptyDefault`, { campaign: outcome.campaignName });
		case "DEFAULT_UNUSABLE":
		case "NO_DEFAULT":
			return t(`${ROUTING_I18N}.summary.emptyNoDefault`);
		case "NONE":
			return t(`${ROUTING_I18N}.summary.emptyNoCampaign`);
	}
};

/**
 * "Auto-assign routing" under the campaign table (PREC-4219, mockup frames 1 and 1b): the list's
 * rules in order, each target that cannot take leads marked skipped, the no-match line, and the
 * button into the editor. A failed read says so; it never reads as "no rules".
 */
export const CampaignRoutingSummary: FunctionComponent<CampaignRoutingSummaryProps> = ({
	settings,
	failed,
	campaigns,
	onEdit,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`${ROUTING_I18N}.${key}`, options) as string;

	const body = (() => {
		if (failed) {
			return <p className="text-sm text-status-error">{ts("summary.loadFailed")}</p>;
		}

		if (!settings) {
			return <p className="text-sm text-text-meta">{ts("summary.loading")}</p>;
		}

		const outcome = fallbackOutcomeOf(settings, campaigns);

		if (settings.rules.length === 0) {
			return (
				<div className="flex flex-col items-start gap-2" data-testid="routing-summary-empty">
					<p className="text-sm text-text-meta">{emptyLine(outcome, t)}</p>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						testId="routing-summary-add"
						title={`+ ${ts("summary.addRule")}`}
						onClick={onEdit}
					/>
				</div>
			);
		}

		const rules = [...settings.rules].sort((a, b) => a.position - b.position);

		return (
			<ol className="flex flex-col gap-1.5" data-testid="routing-summary-rules">
				{rules.map((rule, index) => {
					const name =
						campaigns.find((campaign) => campaign.uuid === rule.campaignUuid)?.name ??
						ts(`targetStates.${rule.targetState}`);
					const usable = isCampaignRoutingTargetUsable(rule.targetState);

					return (
						<li className="flex flex-wrap items-center gap-2 text-sm text-text" key={rule.id}>
							<span>
								{index + 1}. {ruleSummaryText(rule.conditions, name, t)}
							</span>
							{!usable && (
								<span
									className="flex items-center gap-1 text-xs text-status-error"
									data-testid="routing-summary-skipped"
								>
									<Icon className="h-3 w-3" icon={faTriangleExclamation} />
									{ts("summary.skipped", { state: ts(`targetStates.${rule.targetState}`) })}
								</span>
							)}
						</li>
					);
				})}
				<li className="text-sm text-text-meta" data-testid="routing-summary-else">
					{elseLine(outcome, t)}
				</li>
			</ol>
		);
	})();

	return (
		<div
			className="mt-6 flex flex-col gap-3 rounded-xl border border-border-light p-4"
			data-testid="routing-summary"
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-sm font-semibold text-text">{ts("summary.title")}</p>
					<p className="mt-0.5 text-xs leading-5 text-text-meta">{ts("summary.intro")}</p>
				</div>
				{settings && settings.rules.length > 0 && (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						testId="routing-summary-edit"
						title={ts("summary.editRules")}
						onClick={onEdit}
					/>
				)}
			</div>
			{body}
		</div>
	);
};
